import React, { useState, useRef, useEffect } from 'react';
import { paraSlug, type ConfigIa } from '@nyroh/contracts';
import { enfileirarEsteira, resumoDaFila } from '../lib/esteira-fila';
import { Upload, Play, Pause, CheckCircle, Loader2, FileJson, AlertTriangle, MapPin, Phone, Star, Clock, Globe, X, ChevronRight, ExternalLink, Filter, Trash2, PlusCircle, Check, Database, FolderArchive, FolderOpen, FileArchive, Sparkles } from 'lucide-react';
// REMOVED STATIC IMPORT: import JSZip from 'jszip';
import { Client, LeadJSON, QueueItem } from '../types';
import { generateSiteContent } from '../lib/gemini';
import { api } from '../lib/api';

interface MassGeneratorViewProps {
    onAddClients: (clients: Client[]) => void;
    globalSettings: any;
    onViewDetails: (client: Client) => void;
    existingClients: Client[]; // Novo prop para verificar duplicidade no sistema
    onCheckDuplicatesBulk: (items: { name: string, phone: string }[]) => Promise<{ name: boolean, phone: boolean, id: string }[]>;
    
    // Persistence Props (Elevated State)
    queue: QueueItem[];
    selectedIds: Set<string>;
    onUpdateQueue: (queue: QueueItem[]) => void;
    onUpdateSelection: (ids: Set<string>) => void;
}

// FIX: Usar UUID para evitar erros de foreign key/uuid type no Banco
const generateId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

// Função auxiliar para criar URL amigável (Slug) localmente - VERSÃO BLINDADA
/**
 * Slug pela regra do CONTRATO — mesma funcao que o servidor usa e que o
 * `slugSchema` valida. A copia local nao limitava o tamanho e aceitava `_`, que
 * o regex do schema recusa.
 */
const generateSlugLocal = (text: string) => paraSlug(text) || 'projeto';

// Função auxiliar para criar uma "impressão digital" do lead
const getFingerprint = (data: LeadJSON): string => {
    // Prioridade: Telefone limpo (apenas números)
    if (data.phoneNumber) {
        return data.phoneNumber.replace(/\D/g, '');
    }
    // Fallback: Nome + 3 primeiros chars do endereço (se houver)
    const safeName = data.name.toLowerCase().trim();
    const safeAddress = data.address ? data.address.substring(0, 5).toLowerCase() : '';
    return `${safeName}_${safeAddress}`;
};

interface LeadCardProps {
    item: QueueItem;
    isSelected: boolean;
    onToggle?: () => void;
    processing: boolean; // Indica se o modo processamento está ativo (para mostrar overlay)
    onViewDetails: (client: Client) => void;
    onDelete?: () => void;
}

const LeadCard: React.FC<LeadCardProps> = ({ item, isSelected, onToggle, processing, onViewDetails, onDelete }) => {
    const data = item.leadData;
    const isOpen = data.businessStatus?.includes('Aberto') || data.businessStatus === 'OPERATIONAL' || data.businessStatus === 'Operational';
    const hasWebsite = data.website && data.website.length > 5 && data.website !== 'N/A';
    
    const handleViewDossier = (e: React.MouseEvent) => {
        e.stopPropagation();
        const tempClient: Client = {
            id: item.id,
            name: data.name,
            industry: data.categories?.[0] || 'Geral',
            scope: '',
            status: 'draft',
            paymentStatus: 'pending',
            createdAt: Date.now(),
            leadData: data
        };
        onViewDetails(tempClient);
    };

    return (
        <div 
            onClick={onToggle}
            className={`relative group bg-white border rounded-sm p-5 transition-all duration-200 flex flex-col h-full ${
                onToggle ? 'cursor-pointer hover:shadow-md' : ''
            } ${
                isSelected 
                ? 'border-orange-700 ring-1 ring-orange-700 bg-orange-50/10' 
                : 'border-zinc-200 hover:border-slate-400'
            }`}
        >
            {/* Status Overlay durante Processamento */}
            {processing && item.status !== 'waiting' && (
                <div className={`absolute inset-0 z-10 rounded-sm flex items-center justify-center backdrop-blur-[2px] bg-white/50 border-2 ${
                    item.status === 'processing' ? 'border-orange-700' : 
                    item.status === 'done' ? 'border-green-600' : 'border-red-600'
                }`}>
                    <div className="flex flex-col items-center gap-2 bg-white p-4 rounded-sm border border-zinc-200 shadow-xl">
                        {item.status === 'processing' && <Loader2 className="animate-spin text-orange-700" size={24} />}
                        {item.status === 'done' && <CheckCircle className="text-green-600" size={24} />}
                        {item.status === 'error' && <AlertTriangle className="text-red-600" size={24} />}
                        <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                            {item.status === 'processing' ? 'Gerando Site...' : 
                             item.status === 'done' ? 'Salvo & Aprovado' : 'Erro'}
                        </span>
                    </div>
                </div>
            )}

            {/* Botão de Excluir (Só aparece se NÃO estiver processando este item) */}
            {!processing && onDelete && (
                <button 
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="absolute top-4 right-10 md:right-12 p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors z-20"
                    title="Excluir da lista"
                >
                    <Trash2 size={16} />
                </button>
            )}

            {/* Checkbox */}
            {onToggle && (
                <div className={`absolute top-4 right-4 w-5 h-5 rounded-sm border flex items-center justify-center transition-colors ${
                    isSelected ? 'bg-orange-700 border-orange-700' : 'border-zinc-300 bg-white'
                }`}>
                    {isSelected && <CheckCircle size={14} className="text-white" />}
                </div>
            )}

            {/* Header */}
            <div className="flex justify-between items-start mb-3 pr-16">
                <h3 className="font-bold text-slate-900 text-lg leading-tight line-clamp-2">{data.name}</h3>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-4">
                {data.rating && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-yellow-50 text-yellow-700 border border-yellow-200 text-xs font-bold">
                        {data.rating} <Star size={10} fill="currentColor" />
                    </span>
                )}
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs font-medium border ${
                    isOpen ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
                }`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-green-600' : 'bg-red-600'}`}></div>
                    {data.businessStatus || 'Status N/A'}
                </span>
                {data.avatar && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-purple-50 text-purple-700 border border-purple-200 text-[10px] font-bold animate-pulse">
                        Perfil WPP Detectado
                    </span>
                )}
                {data.localImages && data.localImages.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-bold">
                        +{data.localImages.length} Fotos Locais
                    </span>
                )}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-1.5 mb-4">
                {/* Fallback inteligente: Se não tiver categories, usa serviceTags */}
                {(data.categories && data.categories.length > 0 ? data.categories : (data.serviceTags || [])).slice(0, 3).map((cat, i) => (
                    <span key={i} className="px-2 py-0.5 bg-zinc-50 text-slate-600 border border-zinc-200 rounded-sm text-[10px] font-medium">
                        {cat}
                    </span>
                ))}
            </div>

            <p className="text-slate-500 text-xs leading-relaxed line-clamp-3 mb-4 flex-1">
                {data.summary || (data.serviceTags ? `Especialista em: ${data.serviceTags.join(', ')}` : 'Sem descrição disponível.')}
            </p>

            <div className="mt-auto pt-4 border-t border-zinc-100 space-y-3">
                <div className="flex items-center justify-between">
                     <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
                        <Phone size={14} className="text-slate-400 shrink-0" />
                        <span>{data.phoneNumber || 'N/A'}</span>
                    </div>
                    {hasWebsite ? (
                        <a href={data.website || '#'} target="_blank" onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-[10px] text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded-sm border border-blue-100 font-bold">
                            <Globe size={12} /> Website
                        </a>
                    ) : (
                        <div className="flex items-center gap-1 text-[10px] text-slate-400 bg-zinc-50 px-2 py-1 rounded-sm border border-zinc-200 font-bold">
                            <Globe size={12} className="opacity-50" /> Sem site
                        </div>
                    )}
                </div>

                <button 
                    onClick={handleViewDossier}
                    className="w-full mt-2 py-2 bg-white hover:bg-zinc-50 border border-zinc-200 rounded-sm text-xs font-bold text-slate-600 hover:text-slate-900 transition-colors flex items-center justify-center gap-1 hover:border-slate-300"
                >
                    Ver Dossiê Completo <ChevronRight size={12} />
                </button>
            </div>
        </div>
    );
};

export const MassGeneratorView: React.FC<MassGeneratorViewProps> = ({ 
    onAddClients, 
    globalSettings, 
    onViewDetails, 
    existingClients,
    queue,
    selectedIds,
    onUpdateQueue,
    onUpdateSelection,
    onCheckDuplicatesBulk
}) => {
    // Nota: 'queue' e 'selectedIds' agora vêm de props (Estado Elevado)
    // Isso garante persistência ao trocar de aba.
    
    // Controle da Esteira
    const [isPaused, setIsPaused] = useState(true);
    const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
    const [currentProcessingIndex, setCurrentProcessingIndex] = useState(-1);
    const [processingActive, setProcessingActive] = useState(false); // Se a esteira já foi iniciada alguma vez
    const [completedBatchCount, setCompletedBatchCount] = useState(0); // Contador de sites finalizados na sessão
    
    // Filtros Visuais
    const [filterNoSite, setFilterNoSite] = useState(false);
    
    // Feedback de Importação
    const [importFeedback, setImportFeedback] = useState<{added: number, duplicated: number, existing: number} | null>(null);

    // Refs para a Engine (evitam stale closures no loop)
    const isPausedRef = useRef(true);
    const queueRef = useRef<QueueItem[]>(queue);
    const selectedIdsRef = useRef<Set<string>>(selectedIds);

    // Sincronizar os refs com as props sempre que mudarem
    useEffect(() => {
        queueRef.current = queue;
    }, [queue]);

    useEffect(() => {
        selectedIdsRef.current = selectedIds;
    }, [selectedIds]);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        // Limpa feedback anterior
        setImportFeedback(null);

        try {
            const newLeads: QueueItem[] = [];
            const folderMap: Record<string, { json?: any, images: Record<string, string>, avatar?: string, remoteUrls?: string[] }> = {};
            const legacyAvatarMap: Record<string, string> = {};

            // Verifica se algum dos arquivos selecionados é .ZIP
            const zipFiles: File[] = [];
            const normalFiles: File[] = [];

            for (let i = 0; i < files.length; i++) {
                const f = files[i];
                if (f.name.toLowerCase().endsWith('.zip')) {
                    zipFiles.push(f);
                } else {
                    normalFiles.push(f);
                }
            }

            // 1. Processa Arquivos .ZIP (se houver)
            if (zipFiles.length > 0) {
                const module = await import('jszip');
                const JSZip = module.default || module;

                for (const zipFileObj of zipFiles) {
                    const zip = new (JSZip as any)();
                    const loadedZip = await zip.loadAsync(zipFileObj);
                    const fileKeys = Object.keys(loadedZip.files);

                    for (const filename of fileKeys) {
                        if (filename.startsWith('__MACOSX')) continue;
                        const parts = filename.split('/');
                        const folderPath = parts.slice(0, -1).join('/') || 'root';
                        const baseName = parts[parts.length - 1];
                        const lowerName = baseName.toLowerCase();
                        const lowerPath = filename.toLowerCase();

                        // Legacy avatar folder support
                        if (lowerPath.includes('avatars/')) {
                            const avatarBaseName = baseName.split('.')[0];
                            if (avatarBaseName && (lowerPath.endsWith('.jpg') || lowerPath.endsWith('.png') || lowerPath.endsWith('.jpeg') || lowerPath.endsWith('.webp'))) {
                                try {
                                    const base64 = await loadedZip.files[filename].async('base64');
                                    const ext = baseName.split('.').pop()?.toLowerCase();
                                    legacyAvatarMap[avatarBaseName] = `data:image/${ext === 'png' ? 'png' : 'jpeg'};base64,${base64}`;
                                } catch (e) {}
                            }
                        }

                        if (!folderMap[folderPath]) folderMap[folderPath] = { images: {}, remoteUrls: [] };

                        const zipFile = loadedZip.files[filename];
                        if (zipFile.dir) continue;

                        if (lowerName.endsWith('.json')) {
                            try {
                                const content = await zipFile.async('text');
                                folderMap[folderPath].json = JSON.parse(content);
                            } catch (e) { console.error("Erro no JSON ZIP:", filename); }
                        }
                        else if (
                            lowerName.includes('logo') || 
                            lowerName.includes('avatar') || 
                            (lowerName.includes('perfil') && lowerName.includes('whats')) ||
                            lowerName.includes('perfil')
                        ) {
                            try {
                                const base64 = await zipFile.async('base64');
                                const ext = baseName.split('.').pop()?.toLowerCase();
                                folderMap[folderPath].avatar = `data:image/${ext === 'png' ? 'png' : 'jpeg'};base64,${base64}`;
                            } catch (e) { console.error("Erro no avatar ZIP:", filename); }
                        }
                        else if (lowerName.match(/\.(jpg|jpeg|png|webp|avif)$/i)) {
                            try {
                                const base64 = await zipFile.async('base64');
                                const ext = baseName.split('.').pop()?.toLowerCase();
                                folderMap[folderPath].images[baseName] = `data:image/${ext === 'png' ? 'png' : 'jpeg'};base64,${base64}`;
                            } catch (e) { console.error("Erro na foto ZIP:", filename); }
                        }
                        else if (lowerName.endsWith('.txt') && (lowerName.includes('links') || lowerName.includes('fotos'))) {
                            try {
                                const content = await zipFile.async('text');
                                const urls = content.match(/(https?:\/\/[^\s]+)/g) || [];
                                if (urls.length > 0) folderMap[folderPath].remoteUrls = [...(folderMap[folderPath].remoteUrls || []), ...urls];
                            } catch (e) { console.error("Erro link ZIP:", filename); }
                        }
                    }
                }
            }

            // 2. Processa Arquivos de Pastas Locais (se houver)
            if (normalFiles.length > 0) {
                for (let i = 0; i < normalFiles.length; i++) {
                    const file = normalFiles[i];
                    const fullPath = (file as any).webkitRelativePath || file.name;
                    const parts = fullPath.split('/');
                    const folderPath = parts.slice(0, -1).join('/') || 'root';
                    const baseName = parts[parts.length - 1];
                    const lowerName = baseName.toLowerCase();

                    if (!folderMap[folderPath]) folderMap[folderPath] = { images: {}, remoteUrls: [] };

                    if (lowerName.endsWith('.json')) {
                        try {
                            const content = await file.text();
                            folderMap[folderPath].json = JSON.parse(content);
                        } catch (e) { console.error("Erro JSON Local:", fullPath); }
                    }
                    else if (
                        lowerName.includes('logo') || 
                        lowerName.includes('avatar') || 
                        (lowerName.includes('perfil') && lowerName.includes('whats')) ||
                        lowerName.includes('perfil')
                    ) {
                        try {
                            const dataUrl = await new Promise<string>((resolve) => {
                                const reader = new FileReader();
                                reader.onload = () => resolve(reader.result as string);
                                reader.readAsDataURL(file);
                            });
                            folderMap[folderPath].avatar = dataUrl;
                        } catch (e) { console.error("Erro avatar Local:", fullPath); }
                    }
                    else if (lowerName.match(/\.(jpg|jpeg|png|webp|avif)$/i)) {
                        try {
                            const dataUrl = await new Promise<string>((resolve) => {
                                const reader = new FileReader();
                                reader.onload = () => resolve(reader.result as string);
                                reader.readAsDataURL(file);
                            });
                            folderMap[folderPath].images[baseName] = dataUrl;
                        } catch (e) { console.error("Erro foto Local:", fullPath); }
                    }
                    else if (lowerName.endsWith('.txt') && (lowerName.includes('links') || lowerName.includes('fotos'))) {
                        try {
                            const content = await file.text();
                            const urls = content.match(/(https?:\/\/[^\s]+)/g) || [];
                            if (urls.length > 0) folderMap[folderPath].remoteUrls = [...(folderMap[folderPath].remoteUrls || []), ...urls];
                        } catch (e) { console.error("Erro link Local:", fullPath); }
                    }
                }
            }

            // DADOS PARA VERIFICAÇÃO NO BANCO (AGRESIVA)
            const leadsToCheck = Object.values(folderMap)
                .filter(f => f.json && f.json.name)
                .map(f => ({
                    name: f.json.name,
                    phone: f.json.formatted_phone_number || f.json.phoneNumber || f.json.phoneNational || ''
                }));

            // --- INTELIGÊNCIA INSTANTÂNEA: Verificação contra o que já está carregado no FRONT ---
            const frontDuplicateMap = new Map();
            existingClients.forEach(c => {
                const cleanName = c.name?.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
                const rawPhone = c.leadData?.phoneNumber || c.leadData?.formatted_phone_number || c.leadData?.phoneNational || '';
                const cleanPhone = rawPhone.replace(/\D/g, '');
                if (cleanName) frontDuplicateMap.set(cleanName, true);
                if (cleanPhone) frontDuplicateMap.set(cleanPhone, true);
            });

            const dbDuplicates = await onCheckDuplicatesBulk(leadsToCheck);
            const dbDuplicateMap = new Map();
            leadsToCheck.forEach((l, idx) => {
                const cleanName = l.name.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                const cleanPhone = l.phone.replace(/\D/g, '');
                
                // Combina DB + Front para bloqueio total
                const isDupe = (dbDuplicates[idx]?.name || dbDuplicates[idx]?.phone) || (frontDuplicateMap.has(cleanName) || (cleanPhone && frontDuplicateMap.has(cleanPhone)));
                
                if (isDupe) {
                    dbDuplicateMap.set(cleanName, true);
                    if (cleanPhone) dbDuplicateMap.set(cleanPhone, true);
                }
            });

            // Dados para o relatório final
            let countAdded = 0;
            let countDuplicatedInQueue = 0;
            let countAlreadyInSystem = 0;

            // --- MONTAGEM DOS LEADS ---
            for (const folderPath in folderMap) {
                const folder = folderMap[folderPath];
                if (!folder.json || !folder.json.name) continue;

                const rawJson = folder.json;
                
                // Adaptador Universal de Campos (Google Maps vs V1 vs V2)
                const phone = rawJson.formatted_phone_number || rawJson.phoneNumber || rawJson.phoneNational || '';
                
                // DEDUPLICAÇÃO CONTRA O BANCO (HISTÓRICO TOTAL)
                const newNameClean = rawJson.name.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                const newPhoneClean = phone.replace(/\D/g, '');

                if (dbDuplicateMap.has(newNameClean) || (newPhoneClean && dbDuplicateMap.has(newPhoneClean))) {
                    countAlreadyInSystem++;
                    continue;
                }

                const address = rawJson.formatted_address || rawJson.address || '';
                const summary = rawJson.summary || rawJson.editorialDescription || rawJson.reviewSummary || '';
                const businessStatus = rawJson.business_status || rawJson.businessStatus || rawJson.status || '';
                const rating = rawJson.rating || null;
                const reviewCount = rawJson.user_ratings_total || rawJson.reviewCount || 0;
                
                let categories: string[] = rawJson.types || rawJson.categories || (rawJson.mainCategory ? [rawJson.mainCategory] : []);
                
                let amenitiesList: string[] = [];
                if (Array.isArray(rawJson.amenities)) {
                    amenitiesList = rawJson.amenities;
                } else if (rawJson.amenities && typeof rawJson.amenities === 'object') {
                    Object.values(rawJson.amenities).forEach((val: any) => {
                        if (Array.isArray(val)) amenitiesList.push(...val);
                        else if (typeof val === 'string' && val !== 'N/A') amenitiesList.push(val);
                    });
                }

                const mappedLeadData: LeadJSON = {
                    name: rawJson.name,
                    address: address,
                    phoneNumber: phone,
                    placeUri: rawJson.url || rawJson.placeUri || rawJson.mapsLink || '',
                    website: (rawJson.website === 'N/A' || !rawJson.website ? null : rawJson.website),
                    openingHours: rawJson.opening_hours?.weekday_text || rawJson.openingHours || [],
                    businessStatus: businessStatus,
                    priceLevel: rawJson.priceLevel || '',
                    amenities: amenitiesList,
                    rating: rating,
                    reviewCount: reviewCount,
                    topReviews: Array.isArray(rawJson.topReviews) ? rawJson.topReviews : [],
                    reviews: Array.isArray(rawJson.reviews) ? rawJson.reviews.map((r: any) => ({
                        author_name: r.author_name || r.authorName || 'Cliente',
                        text: r.text || r.comment || '',
                        rating: r.rating || 5,
                        profile_photo_url: r.profile_photo_url || r.profilePhotoUrl || '',
                        relative_time_description: r.relative_time_description || r.timeDescription || ''
                    })) : [],
                    categories: categories,
                    summary: summary,
                    photoAnalysis: rawJson.photoAnalysis || '',
                    seoKeywords: rawJson.seoKeywords || [],
                    blueprint: rawJson.blueprint,
                    serviceTags: rawJson.serviceTags || []
                };

                // VINCULA IMAGENS DO NOVO FORMATO
                if (folder.avatar) mappedLeadData.avatar = folder.avatar;
                if (Object.keys(folder.images).length > 0) {
                    mappedLeadData.localImages = Object.values(folder.images);
                }
                if (folder.remoteUrls && folder.remoteUrls.length > 0) {
                    mappedLeadData.remoteImages = folder.remoteUrls;
                }

                // VINCULA AVATAR DO FORMATO LEGACY (se não tiver no novo)
                if (!mappedLeadData.avatar) {
                    const fileNameGuess = folderPath !== 'root' ? folderPath.split('/').pop() : '';
                    if (fileNameGuess && legacyAvatarMap[fileNameGuess]) {
                        mappedLeadData.avatar = legacyAvatarMap[fileNameGuess];
                    }
                }

                // DEDUPLICAÇÃO CONTRA FILA ATUAL
                const fingerprint = getFingerprint(mappedLeadData);
                const existsInQueue = queueRef.current.some(q => 
                    q.leadData.name.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === newNameClean || 
                    getFingerprint(q.leadData) === fingerprint
                ) || newLeads.some(nl => nl.leadData.name.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') === newNameClean);

                if (existsInQueue) {
                    countDuplicatedInQueue++;
                    continue;
                }

                newLeads.push({
                    id: generateId(),
                    leadData: mappedLeadData,
                    status: 'waiting'
                });
                countAdded++;
            }

            // Atualiza a Fila (Append via Prop)
            const updatedQueue = [...queueRef.current, ...newLeads];
            onUpdateQueue(updatedQueue);
            
            // Seleciona automaticamente os novos (opcional, mas boa UX)
            const newSet = new Set(selectedIds);
            newLeads.forEach(l => newSet.add(l.id));
            onUpdateSelection(newSet);

            // Feedback
            setImportFeedback({
                added: countAdded,
                duplicated: countDuplicatedInQueue,
                existing: countAlreadyInSystem
            });

            // Limpa o input para permitir subir o mesmo arquivo se quiser
            e.target.value = '';

        } catch (err) { 
            console.error("Erro na importação m massa:", err);
            alert("Erro ao processar arquivos. Verifique o console para mais detalhes."); 
        }
    };

    const toggleSelection = (id: string) => {
        const newSet = new Set(selectedIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        onUpdateSelection(newSet);
    };

    const removeLead = (id: string) => {
        const newQueue = queue.filter(l => l.id !== id);
        onUpdateQueue(newQueue);
        
        const newSet = new Set(selectedIds);
        newSet.delete(id);
        onUpdateSelection(newSet);
    };

    // Lógica de Visualização (Respeita o Filtro)
    const displayedLeads = queue.filter(item => {
        if (filterNoSite) {
            return !item.leadData.website || item.leadData.website.length <= 5 || item.leadData.website === 'N/A';
        }
        return true;
    });

    // Lógica de Início da Esteira
    /**
     * ========================================================================
     * ENFILEIRAR PARA O WORKER — a esteira que sobrevive a fechar a aba
     * ========================================================================
     *
     * A esteira de cima roda no NAVEGADOR: um laço com `setTimeout` que chama a
     * IA lead a lead. Fechar a aba mata o lote e o progresso não sobrevive a um
     * F5 — com 116 leads, é uma pessoa presa numa tela aberta.
     *
     * Este caminho cria os clientes e enfileira um `gerar_site` por cliente. O
     * worker já sabia processar esse tipo; o que faltava era alguém alimentar a
     * fila. Daqui em diante o andamento vem do SERVIDOR, não de um contador que
     * morre com a aba.
     */
    const [enfileirando, setEnfileirando] = useState(false);
    const [resumoFila, setResumoFila] = useState<Awaited<ReturnType<typeof resumoDaFila>> | null>(null);

    /*
      O QUE A ESTEIRA VAI FAZER — dito ANTES de clicar.

      Esta tela enfileirava sem dizer nada sobre IA, e o worker gerava tudo por
      template. Quem clicava acreditava estar produzindo sites de IA e recebia
      cinco layouts se repetindo em cem clientes, sem nenhuma mensagem no meio do
      caminho. O modo agora vem do servidor e aparece ao lado do botão.
    */
    const [configIa, setConfigIa] = useState<ConfigIa | null>(null);

    const atualizarResumoFila = async () => {
        try {
            setResumoFila(await resumoDaFila());
        } catch (e) {
            // Sem sessão de operador ou API fora. Não é erro fatal: a tela só
            // deixa de mostrar o andamento, e diz isso em vez de mentir zero.
            console.warn('[esteira] não consegui ler o resumo da fila:', e);
            setResumoFila(null);
        }

        /*
          `try` SEPARADO, de propósito.

          Com os dois no mesmo bloco, uma falha ao ler `ia.config` — a segunda
          chamada — caía no `catch` e apagava o resumo da fila que a primeira
          tinha acabado de carregar com dado bom. Duas leituras independentes não
          podem compartilhar tratamento de erro: uma falha derruba a outra.
        */
        try {
            setConfigIa(await api.chamar('ia.config', {}));
        } catch (e) {
            console.warn('[esteira] não consegui ler a configuração de IA:', e);
            setConfigIa(null);
        }
    };

    const enfileirarSelecionados = async () => {
        const alvos = displayedLeads.filter((l) => selectedIds.has(l.id));
        if (alvos.length === 0) return;

        setEnfileirando(true);
        try {
            const r = await enfileirarEsteira(alvos.map((l) => ({ leadData: l.leadData })));
            await atualizarResumoFila();
            alert(
                `${r.enfileirados} site(s) na fila do worker.\n\n` +
                `${r.criados} cliente(s) criado(s)` +
                (r.ignorados ? `, ${r.ignorados} já existiam` : '') +
                (r.jaNaFila ? `\n${r.jaNaFila} já estavam na fila` : '') +
                `\n\nPode fechar esta aba: o worker continua.`,
            );
        } catch (e) {
            console.error('[esteira] falha ao enfileirar:', e);
            alert(e instanceof Error ? e.message : 'Não consegui enfileirar.');
        } finally {
            setEnfileirando(false);
        }
    };

    useEffect(() => {
        void atualizarResumoFila();
        // Um pulso a cada 10s enquanto a tela está aberta. Barato (uma contagem
        // agregada) e é o que mostra o worker andando sem precisar recarregar.
        const t = setInterval(() => void atualizarResumoFila(), 10_000);
        return () => clearInterval(t);
    }, []);
    const startFactory = () => {
        // Validação: Tem algo selecionado?
        const hasSelection = displayedLeads.some(item => selectedIds.has(item.id));
        
        if (!hasSelection) {
            alert("Nenhum lead selecionado para processamento.");
            return;
        }

        setProcessingActive(true);
        setIsPaused(false);
        isPausedRef.current = false;
        processQueue();
    };

    const togglePause = () => {
        const newState = !isPaused;
        setIsPaused(newState);
        isPausedRef.current = newState;
        if (!newState) processQueue();
    };

    const handleCheckDatabaseDuplicates = async () => {
        if (queue.length === 0) {
            alert("A fila está vazia.");
            return;
        }
        
        console.log("Botão 'Limpar Duplicados' acionado.");
        setIsCheckingDuplicates(true);
        try {
            const leadsToCheck = queue.map(item => ({
                name: item.leadData.name,
                phone: item.leadData.phoneNumber
            }));
            
            console.log(`Verificando ${leadsToCheck.length} leads contra o banco (Nomes e Telefones)...`);
            const duplicateResults = await onCheckDuplicatesBulk(leadsToCheck);
            
            if (!duplicateResults) {
                console.warn("Retorno de duplicados foi nulo.");
                alert("Não foi possível obter resposta do servidor.");
                return;
            }

            const newQueue = queue.filter((item, idx) => {
                const res = duplicateResults[idx];
                return !(res?.name || res?.phone);
            });
            
            const removedCount = queue.length - newQueue.length;
            onUpdateQueue(newQueue);
            
            // Re-sync selection
            const newIds = new Set(newQueue.map(item => item.id));
            const newSelection = new Set(Array.from(selectedIds).filter(id => newIds.has(id)));
            onUpdateSelection(newSelection);
            
            if (removedCount > 0) {
                alert(`Sucesso! ${removedCount} leads já existentes no seu Banco de Registros (mesmo antigos) foram identificados e removidos.`);
            } else {
                alert("Limpeza concluída: Nenhum dos leads na sua fila foi encontrado no banco de registros.");
            }
        } catch (err) {
            console.error("Erro no handleCheckDatabaseDuplicates:", err);
            alert("Ocorreu um erro ao tentar limpar duplicados.");
        } finally {
            setIsCheckingDuplicates(false);
        }
    };

    const processQueue = async () => {
        if (isPausedRef.current) return;

        // Encontra o próximo item que está WAITING E que foi SELECIONADO (usa ref para pegar estado atualizado)
        const nextIdx = queueRef.current.findIndex(item => item.status === 'waiting' && selectedIdsRef.current.has(item.id));
        
        if (nextIdx === -1) {
            setIsPaused(true);
            isPausedRef.current = true;
            setProcessingActive(false); 
            return;
        }

        setCurrentProcessingIndex(nextIdx);
        updateQueueItemStatus(nextIdx, 'processing');

        const item = queueRef.current[nextIdx];
        
        try {
            const industry = item.leadData.categories?.[0] || item.leadData.serviceTags?.[0] || "Geral";
            const tempClient: Client = {
                id: item.id,
                name: item.leadData.name,
                industry: industry,
                scope: item.leadData.blueprint 
                    ? `[BLUEPRINT IMPORTADO] ${item.leadData.summary}`
                    : `[Geração Automática V2] Tags: ${item.leadData.serviceTags?.join(', ')}`,
                status: 'generating',
                paymentStatus: 'pending',
                createdAt: Date.now(),
                leadData: item.leadData,
                trialHours: globalSettings.defaultTrialValue * (globalSettings.defaultTrialUnit === 'days' ? 24 : 1)
            };

            const html = await generateSiteContent(tempClient);

            // --- AUTO APROVAÇÃO E GERAÇÃO DE SLUG ROBUSTA ---
            let host = window.location.hostname;
            if (host.startsWith('www.')) host = host.substring(4);
            
            // Correção Fundamental: Usa a função blindada + Sufixo Aleatório (igual manual)
            const baseSlug = generateSlugLocal(tempClient.name);
            const randomSuffix = generateId().substring(0,3);
            const generatedSlug = `${baseSlug}-${randomSuffix}`;
            
            const subdomain = `${generatedSlug}.${host}`;

            const finalClient: Client = {
                ...tempClient,
                status: 'approved', // JÁ NASCE APROVADO!
                slug: generatedSlug,
                subdomain: subdomain,
                siteContent: html
            };

            updateQueueItemStatus(nextIdx, 'done', finalClient);
            
            // Envia para o App salvar imediatamente (Persistência Instantânea no Supabase)
            onAddClients([finalClient]);
            setCompletedBatchCount(prev => prev + 1);

            // OTIMIZAÇÃO DE MEMÓRIA CRÍTICA (Garbage Collection):
            // Descarta o array pesado de base64 da memória do navegador após salvar com sucesso no banco,
            // mantendo o item leve (status 'done', links e metadados intactos) para prevenir tela branca / estouro de RAM.
            updateQueueItemStatus(nextIdx, 'done', finalClient, true);

        } catch (error) {
            console.error(error);
            updateQueueItemStatus(nextIdx, 'error');
        }

        if (!isPausedRef.current) {
            // Pequeno delay para não sobrecarregar
            setTimeout(() => processQueue(), 1000);
        }
    };

    const updateQueueItemStatus = (index: number, status: QueueItem['status'], resultClient?: Client, purgeHeavyImages: boolean = false) => {
        // Copia a fila atual do REF (que é a mais recente)
        const newQueue = [...queueRef.current];
        
        if (purgeHeavyImages && newQueue[index]) {
            // Remove arrays de base64 pesados da fila em memória para aliviar a RAM
            const lightLeadData = {
                ...newQueue[index].leadData,
                localImages: undefined,
                avatar: undefined
            };
            newQueue[index] = { 
                ...newQueue[index], 
                leadData: lightLeadData, 
                status, 
                resultClient 
            };
        } else {
            newQueue[index] = { ...newQueue[index], status, resultClient };
        }
        
        // Atualiza via Prop para persistir no pai
        onUpdateQueue(newQueue);
    };

    // --- RENDER ---

    // 1. Estado Zero (Nenhum lead carregado)
    if (queue.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-10 animate-fade-in bg-zinc-50">
                <div className="max-w-xl w-full text-center space-y-8">
                    <div>
                        <h2 className="text-3xl font-bold text-slate-900 mb-2">Fábrica de Sites em Massa</h2>
                        <p className="text-slate-500">Importe arquivos ZIP com leads para iniciar a esteira.</p>
                        <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 rounded-full border border-slate-200 text-[11px] font-semibold text-slate-600">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                            Deduplicação ativa contra Banco do Sistema e Diretório da Nyroh API (10.9k+ leads)
                        </div>
                        {importFeedback && (
                            <div className="mt-4 p-4 bg-white border border-zinc-200 rounded-sm text-sm text-slate-600 flex flex-col gap-1 shadow-sm">
                                <span className="text-green-600 font-bold flex items-center justify-center gap-2">
                                    <CheckCircle size={14} /> Importação anterior: {importFeedback.added} adicionados.
                                </span>
                                {importFeedback.existing > 0 && <span>• {importFeedback.existing} ignorados (já clientes).</span>}
                                {importFeedback.duplicated > 0 && <span>• {importFeedback.duplicated} ignorados (duplicados no arquivo).</span>}
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Opção 1: Upload ZIP */}
                        <label className="flex flex-col items-center justify-center p-6 h-56 border-2 border-zinc-300 border-dashed rounded-lg cursor-pointer hover:border-orange-700 hover:bg-white transition-all group bg-white/60">
                            <FolderArchive className="text-slate-400 group-hover:text-orange-700 mb-3" size={36} />
                            <p className="mb-1 text-sm font-bold text-slate-900 group-hover:text-orange-700">Subir Arquivo .ZIP</p>
                            <p className="text-xs text-slate-500 text-center">Ideal para lotes grandes comprimidos</p>
                            <span className="mt-3 inline-block px-3 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-[11px] font-bold rounded-sm">
                                Selecionar .ZIP
                            </span>
                            <input 
                                type="file" 
                                className="hidden" 
                                accept=".zip"
                                multiple
                                onChange={handleFileUpload}
                            />
                        </label>

                        {/* Opção 2: Upload Pasta */}
                        <label className="flex flex-col items-center justify-center p-6 h-56 border-2 border-zinc-300 border-dashed rounded-lg cursor-pointer hover:border-slate-800 hover:bg-white transition-all group bg-white/60">
                            <FolderOpen className="text-slate-400 group-hover:text-slate-800 mb-3" size={36} />
                            <p className="mb-1 text-sm font-bold text-slate-900 group-hover:text-slate-800">Subir Pasta de Leads</p>
                            <p className="text-xs text-slate-500 text-center">Selecione uma pasta com subpastas</p>
                            <span className="mt-3 inline-block px-3 py-1 bg-zinc-100 border border-zinc-300 text-slate-700 text-[11px] font-bold rounded-sm">
                                Selecionar Pasta
                            </span>
                            <input 
                                type="file" 
                                className="hidden" 
                                onChange={handleFileUpload}
                                {...({
                                    webkitdirectory: "",
                                    directory: "",
                                    multiple: true
                                } as any)}
                            />
                        </label>
                    </div>
                </div>
            </div>
        );
    }

    // 2. Estado com Dados (Grid + Toolbar)
    return (
        <div className="flex flex-col h-full p-6 md:p-8 animate-fade-in bg-zinc-50">
            
            {/* Toolbar Superior */}
            <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-6 mb-8 sticky top-0 bg-zinc-50/95 backdrop-blur z-20 py-4 border-b border-zinc-200">
                
                {/* Lado Esquerdo: Info e Status */}
                <div className="flex items-center gap-6">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                            Linha de Produção
                            {processingActive && !isPaused && (
                                <span className="relative flex h-3 w-3">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                                </span>
                            )}
                        </h2>
                        <div className="text-slate-500 text-sm flex gap-2 items-center flex-wrap">
                            <span>{queue.length} na fila.</span>
                            {completedBatchCount > 0 && (
                                <span className="text-xs bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded-sm border border-emerald-200 flex items-center gap-1 font-bold">
                                    <CheckCircle size={11} className="text-emerald-600" /> {completedBatchCount} gerados e salvos no banco
                                </span>
                            )}
                            {importFeedback && (
                                <span className="text-xs bg-white px-2 py-0.5 rounded-sm border border-zinc-200 flex items-center gap-1 font-semibold" title="Relatório da última importação">
                                    <Check size={10} className="text-green-600" /> +{importFeedback.added} novos
                                    {(importFeedback.duplicated > 0 || importFeedback.existing > 0) && (
                                        <span className="text-slate-400">({importFeedback.duplicated + importFeedback.existing} ignorados)</span>
                                    )}
                                    <button onClick={() => setImportFeedback(null)} className="hover:text-red-500 ml-1"><X size={10} /></button>
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Botões de Adicionar Mais (Upload ZIP e Pasta) */}
                    <div className="flex items-center gap-2">
                        {/* Upload ZIP */}
                        <label className="flex items-center gap-2 px-3.5 py-2 bg-white hover:bg-zinc-50 border border-zinc-200 rounded-sm cursor-pointer transition-colors group shadow-sm" title="Subir um ou mais arquivos .ZIP">
                            <FolderArchive size={16} className="text-orange-700 group-hover:text-orange-600" />
                            <span className="text-xs font-bold text-slate-700 group-hover:text-slate-900">Subir .ZIP</span>
                            <input 
                                type="file" 
                                className="hidden" 
                                accept=".zip"
                                multiple
                                onChange={handleFileUpload}
                            />
                        </label>

                        {/* Upload Pasta */}
                        <label className="flex items-center gap-2 px-3.5 py-2 bg-white hover:bg-zinc-50 border border-zinc-200 rounded-sm cursor-pointer transition-colors group shadow-sm" title="Selecionar Pasta Local de Leads">
                            <FolderOpen size={16} className="text-slate-600 group-hover:text-slate-900" />
                            <span className="text-xs font-bold text-slate-700 group-hover:text-slate-900">Subir Pasta</span>
                            <input 
                                type="file" 
                                className="hidden" 
                                onChange={handleFileUpload}
                                {...({
                                    webkitdirectory: "",
                                    directory: "",
                                    multiple: true
                                } as any)}
                            />
                        </label>
                    </div>
                </div>

                {/* Lado Direito: Controles */}
                <div className="flex flex-wrap gap-3 items-center">
                    
                    {/* Botão de Check Database */}
                    <button
                        onClick={handleCheckDatabaseDuplicates}
                        disabled={isCheckingDuplicates || queue.length === 0}
                        className="flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-bold bg-white border border-zinc-200 text-slate-700 hover:bg-zinc-50 transition-all shadow-sm disabled:opacity-50"
                    >
                        {isCheckingDuplicates ? <Loader2 className="animate-spin" size={14} /> : <Database size={14} />}
                        {isCheckingDuplicates ? 'Limpando...' : 'Limpar Duplicados (Banco)'}
                    </button>
                    
                    {/* Filtros */}
                    <button 
                        onClick={() => setFilterNoSite(!filterNoSite)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-bold transition-all border ${
                            filterNoSite ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-white border-zinc-200 text-slate-500'
                        }`}
                    >
                        <Filter size={14} /> {filterNoSite ? 'Sem Site (Ativo)' : 'Sem Site'}
                    </button>

                    <div className="h-6 w-[1px] bg-zinc-200 hidden md:block"></div>

                    {/* Seleção */}
                    <button 
                        onClick={() => {
                            if (selectedIds.size === displayedLeads.length) onUpdateSelection(new Set());
                            else onUpdateSelection(new Set(displayedLeads.map(l => l.id)));
                        }}
                        className="text-slate-500 hover:text-slate-900 text-xs font-bold px-2 uppercase tracking-wide"
                    >
                        {selectedIds.size === displayedLeads.length && displayedLeads.length > 0 ? 'Desmarcar Tudo' : 'Selecionar Tudo'}
                    </button>

                    {/* Botão de Ação Principal (Play/Pause) */}
                    {processingActive ? (
                        <button 
                            onClick={togglePause}
                            className={`px-6 py-3 rounded-sm font-bold flex items-center gap-2 text-sm transition-all shadow-md ${
                                isPaused 
                                ? 'bg-green-600 hover:bg-green-500 text-white' 
                                : 'bg-yellow-500 hover:bg-yellow-400 text-white'
                            }`}
                        >
                            {isPaused ? <Play size={16} fill="currentColor" /> : <Pause size={16} fill="currentColor" />}
                            {isPaused ? 'Continuar Produção' : 'Pausar'}
                        </button>
                    ) : (
                        <div className="flex items-center gap-2">
                            <button 
                                onClick={startFactory}
                                disabled={selectedIds.size === 0}
                                className="bg-slate-900 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-sm font-bold text-sm flex items-center gap-2 shadow-lg shadow-slate-900/10 transition-all"
                            >
                                <Play size={16} fill="currentColor" /> Iniciar Esteira ({Array.from(selectedIds).filter(id => displayedLeads.find(l => l.id === id)).length})
                            </button>
                            {/*
                              O mesmo lote, mas processado pelo WORKER. Fechar a
                              aba deixa de interromper: o andamento passa a vir da
                              fila no servidor.
                            */}
                            <button
                                onClick={enfileirarSelecionados}
                                disabled={selectedIds.size === 0 || enfileirando}
                                title="Cria os clientes e enfileira a geração. O worker processa mesmo com a aba fechada."
                                className="bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-slate-800 px-5 py-3 rounded-sm font-bold text-sm flex items-center gap-2 transition-all"
                            >
                                {enfileirando ? <Loader2 size={16} className="animate-spin" /> : <Database size={16} />}
                                Enfileirar para o worker
                            </button>

                            {/*
                              O modo, em voz alta. Sem isto o "só saiu template"
                              não tem sintoma: a esteira termina com sucesso e os
                              sites ficam parecidos, e a causa não aparece em
                              lugar nenhum da interface.
                            */}
                            {configIa && (
                                <span
                                    className={`text-xs font-semibold px-3 py-2 rounded-sm border ${
                                        configIa.modoGeracao === 'ia' && configIa.fonteChave !== 'nenhuma'
                                            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                            : 'bg-amber-50 border-amber-200 text-amber-900'
                                    }`}
                                    title="Configurado na aba Inteligência Artificial"
                                >
                                    {configIa.fonteChave === 'nenhuma'
                                        ? 'Sem chave do Gemini: sairá template'
                                        : configIa.modoGeracao === 'template'
                                          ? 'Modo template: a IA não será chamada'
                                          : `IA ligada · ${configIa.modelo}`}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/*
              O ANDAMENTO DO WORKER.

              Vem de `fila.resumo`, no servidor — a única fonte que sabe o que já
              foi processado. O contador da esteira antiga vivia no navegador e
              morria com a aba, então "116 na fila" não queria dizer nada depois
              de um F5.
            */}
            {resumoFila && (resumoFila.porTipo['gerar_site'] ?? 0) > 0 && (
                <div className="mb-6 bg-white border border-slate-200 rounded-sm px-5 py-4">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
                        <span className="font-bold text-slate-900 uppercase tracking-widest text-[10px]">
                            Fila do worker
                        </span>
                        <span className="text-slate-500">
                            <b className="text-slate-900">{resumoFila.porStatus.pendente ?? 0}</b> aguardando
                        </span>
                        <span className="text-slate-500">
                            <b className="text-blue-700">{resumoFila.porStatus.processando ?? 0}</b> processando
                        </span>
                        <span className="text-slate-500">
                            <b className="text-green-700">{resumoFila.porStatus.concluido ?? 0}</b> concluídos
                        </span>
                        {(resumoFila.porStatus.falhou ?? 0) > 0 && (
                            <span className="text-slate-500">
                                <b className="text-red-600">{resumoFila.porStatus.falhou}</b> falharam
                            </span>
                        )}
                        {resumoFila.deadLetter > 0 && (
                            <span className="text-amber-700">
                                <b>{resumoFila.deadLetter}</b> esgotaram as tentativas
                            </span>
                        )}
                        <span className="text-slate-400 ml-auto">atualiza sozinho a cada 10s</span>
                    </div>
                    {(resumoFila.porStatus.pendente ?? 0) > 0 && (resumoFila.porStatus.processando ?? 0) === 0 && (
                        <p className="text-[11px] text-amber-700 mt-2">
                            Há trabalho parado e ninguém processando. O worker
                            (<span className="font-mono">packages/worker</span>) provavelmente não está no ar.
                        </p>
                    )}
                </div>
            )}

            {/* Grid de Cards com Janela de Performance de Renderização (Previne tela branca / lag do DOM em listas de 1000+) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-20 overflow-y-auto custom-scrollbar">
                {displayedLeads.slice(0, 60).map(item => (
                    <LeadCard 
                        key={item.id} 
                        item={item} 
                        isSelected={selectedIds.has(item.id)}
                        onToggle={() => toggleSelection(item.id)}
                        processing={processingActive} // Passa o estado global de processamento
                        onViewDetails={onViewDetails}
                        onDelete={() => removeLead(item.id)}
                    />
                ))}
            </div>
            {displayedLeads.length > 60 && (
                <div className="text-center py-3 text-xs text-slate-400 font-medium">
                    Mostrando os primeiros 60 de {displayedLeads.length} leads para manter a navegação instantânea e livre de sobrecarga de memória.
                </div>
            )}
        </div>
    );
};